// Segmented control (handoff bundle's forms/SegmentedControl): sunken track,
// the active option floats on a card surface with a soft shadow.

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  options: SegmentedOption<T>[] | T[];
  value: T;
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  /** Fill the container width, options sharing it equally. */
  block?: boolean;
  className?: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = 'md',
  block = false,
  className,
}: SegmentedControlProps<T>) {
  const opts: SegmentedOption<T>[] = options.map((o) =>
    typeof o === 'string' ? { value: o, label: o } : o
  );
  const pad = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';
  return (
    <div
      role="tablist"
      className={[
        'inline-flex gap-0.5 rounded border border-slate-200 bg-paper-sunken p-0.5',
        block ? 'flex w-full' : '',
        className ?? '',
      ].join(' ')}
    >
      {opts.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.value)}
            className={[
              'whitespace-nowrap rounded-sm font-medium transition duration-150 ease-out',
              pad,
              block ? 'flex-1' : '',
              active
                ? 'bg-card text-slate-900 shadow-xs'
                : 'text-slate-500 hover:text-slate-900',
            ].join(' ')}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
